import { forwardRef, FunctionComponent, InputHTMLAttributes, isValidElement, ReactElement } from 'react';

import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { CheckboxVariants, CheckboxVariantsStyles } from 'constants/checkbox';
import { useOpen } from 'hooks/useOpen';
import { ErrorMessage, Skeleton } from 'shared-components';
import { cn } from 'utils/global';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: FunctionComponent<unknown> | string | ReactElement;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  inputId?: string;
  labelHtmlFor?: string;
  name?: string;
  value?: string;
  dataCy?: string;
  error?: string;
  tooltipContent?: string;
  isLoading?: boolean;
  variant?: CheckboxVariants;
}

const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      error,
      inputClassName,
      labelClassName,
      className,
      labelHtmlFor,
      name,
      dataCy,
      onChange,
      tooltipContent,
      isLoading,
      variant = CheckboxVariants.PRIMARY,
      ...props
    },
    ref
  ) => {
    const [isShownTooltip, handleShowTooltip, handleCloseTooltip] = useOpen();

    const { t } = useTranslation();

    const renderedLabel = (): ReactElement | string => {
      if (!label) {
        return '';
      }

      switch (typeof label) {
        case 'function':
          const Component = label as FunctionComponent;
          return <Component />;

        case 'object':
          if (isValidElement(label)) {
            return label;
          }
          break;

        case 'string':
          return t(label);

        default:
          return '';
      }

      return '';
    };

    const inputStyles =
      'dark:checked:border-primary-500 dark:hover:border-primary-500 dark:hover:checked:border-primary-500 dark:checked:bg-primary-500 dark:hover:checked:bg-primary-500 dark:hover:disabled:border-primary-300, dark:checked:disabled:bg-primary-300 dark:checked:disabled:border-primary-300 dark:disabled:hover:border-primary-300';

    return (
      <div data-cy={dataCy} className={className}>
        <label
          id={name}
          onMouseEnter={handleShowTooltip}
          onMouseLeave={handleCloseTooltip}
          htmlFor={labelHtmlFor}
          className={cn('flex items-center cursor-pointer w-fit', {
            'pointer-events-none opacity-50': props.disabled,
          })}
        >
          {isLoading ? (
            <Skeleton className={cn(inputClassName, inputStyles)} />
          ) : (
            <input
              id={labelHtmlFor}
              type="checkbox"
              name={name}
              onChange={onChange}
              ref={ref}
              {...props}
              className={cn(inputStyles, CheckboxVariantsStyles[variant], inputClassName)}
            />
          )}
          {label && (
            <span
              className={cn('ml-2 text-bodyMd text-neutral-700 dark:text-neutral-300', labelClassName, {
                'text-neutral-400': props.disabled,
              })}
            >
              {renderedLabel()}
            </span>
          )}
        </label>

        {error && <ErrorMessage>{t(error)}</ErrorMessage>}
        {tooltipContent && (
          <Tooltip
            anchorId={name}
            positionStrategy="fixed"
            place="right"
            className="tooltip-styles max-w-[170px] font-light"
            isOpen={isShownTooltip}
            clickable
          >
            {t(tooltipContent)}
          </Tooltip>
        )}
      </div>
    );
  }
);

export default Checkbox;

Checkbox.displayName = 'Checkbox';
